import { useState, useEffect } from 'react';
import {
  Loader2, CheckCircle2, AlertTriangle, XCircle, Shield,
  ChevronDown, ChevronRight, Play, Eye, Zap,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';


function StatusIcon({ status }) {
  if (status === 'ok') return <CheckCircle2 className="h-4 w-4 text-green-600" />;
  if (status === 'error') return <XCircle className="h-4 w-4 text-red-500" />;
  if (status === 'refused') return <Shield className="h-4 w-4 text-orange-500" />;
  if (status === 'orphan_warning') return <AlertTriangle className="h-4 w-4 text-red-600" />;
  return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
}


function EntityRow({ entity, onTest, onPreview, onApply, testResult, writeResult, testing, writing }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border rounded-lg">
      {/* Summary row */}
      <div
        className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/30"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded
          ? <ChevronDown className="h-3.5 w-3.5 shrink-0" />
          : <ChevronRight className="h-3.5 w-3.5 shrink-0" />}

        <span className="text-sm font-medium flex-1">{entity.canonical_name}</span>
        <code className="text-xs text-muted-foreground">{entity.entity_id}</code>

        {/* Key present badge */}
        {entity.credential_seeded ? (
          entity.key_present ? (
            <Badge variant="default" className="bg-green-600 hover:bg-green-700 text-[10px]">
              Key present
            </Badge>
          ) : (
            <Badge variant="destructive" className="text-[10px]">Key missing</Badge>
          )
        ) : (
          <Badge variant="outline" className="text-[10px]">Not seeded</Badge>
        )}

        {/* Last connection status */}
        {entity.last_connection_status && (
          <Badge
            variant={entity.last_connection_status === 'ok' ? 'default' : 'destructive'}
            className={entity.last_connection_status === 'ok' ? 'bg-green-600 hover:bg-green-700 text-[10px]' : 'text-[10px]'}
          >
            {entity.last_connection_status}
          </Badge>
        )}
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t px-4 py-3 space-y-3 bg-muted/10">
          {/* Action buttons */}
          <div className="flex gap-2 flex-wrap">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onTest(entity.entity_id)}
              disabled={testing || !entity.key_present}
            >
              {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Play className="h-3.5 w-3.5 mr-1.5" />}
              Read + Scope Check
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => onPreview(entity.entity_id)}
              disabled={writing || !entity.key_present}
            >
              <Eye className="h-3.5 w-3.5 mr-1.5" />
              Preview Write
            </Button>
            <Button
              size="sm"
              variant="default"
              className="bg-orange-600 hover:bg-orange-700"
              onClick={() => {
                if (window.confirm(
                  `This will create and immediately delete a draft invoice in ${entity.canonical_name}. Proceed?`
                )) {
                  onApply(entity.entity_id);
                }
              }}
              disabled={writing || !entity.key_present}
            >
              {writing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Zap className="h-3.5 w-3.5 mr-1.5" />}
              Apply Write Proof
            </Button>
          </div>

          {/* Read + scope result */}
          {testResult && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <StatusIcon status={testResult.status} />
                <span className="text-sm font-medium">
                  Read + Scope: {testResult.status}
                </span>
              </div>

              {testResult.error && (
                <p className="text-xs text-red-600">{testResult.error}</p>
              )}

              {testResult.this_key_controls && (
                <div className="rounded border px-3 py-2 bg-muted/20">
                  <p className="text-xs font-medium text-muted-foreground mb-1">This key controls:</p>
                  <p className="text-sm font-semibold">{testResult.this_key_controls}</p>
                  {testResult.resolved_company && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Company ID: {testResult.resolved_company.id} | Reg: {testResult.resolved_company.reg_no}
                    </p>
                  )}
                </div>
              )}

              {testResult.scopes !== undefined && (
                <div className="rounded border px-3 py-2 bg-muted/20">
                  <p className="text-xs font-medium text-muted-foreground mb-1">Token scopes:</p>
                  {testResult.scopes ? (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {testResult.scopes.map((s) => (
                        <Badge
                          key={s}
                          variant="outline"
                          className={`text-[10px] font-mono ${s.endsWith(':all') ? 'border-green-500 text-green-700' : ''}`}
                        >
                          {s}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">Scopes not returned by /me</p>
                  )}
                  {testResult.write_capable !== undefined && (
                    <p className="text-xs mt-1">
                      Write capable: {' '}
                      <span className={typeof testResult.write_capable === 'boolean'
                        ? (testResult.write_capable ? 'text-green-600 font-medium' : 'text-red-600 font-medium')
                        : 'text-yellow-600'
                      }>
                        {String(testResult.write_capable)}
                      </span>
                    </p>
                  )}
                </div>
              )}

              {testResult.data_access && (
                <div className="rounded border px-3 py-2 bg-muted/20">
                  <p className="text-xs font-medium text-muted-foreground mb-1">Data access ({testResult.data_access.endpoint}):</p>
                  <p className="text-xs">
                    Status: {testResult.data_access.status_code}
                    {testResult.data_access.items_available && ` | Sample count: ${testResult.data_access.sample_count}`}
                  </p>
                  {testResult.data_access.sample_keys && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {testResult.data_access.sample_keys.map((k) => (
                        <Badge key={k} variant="outline" className="text-[10px] font-mono">{k}</Badge>
                      ))}
                    </div>
                  )}
                  {testResult.data_access.error && (
                    <p className="text-xs text-red-600 mt-1">{testResult.data_access.error}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Write proof result */}
          {writeResult && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <StatusIcon status={writeResult.status || (writeResult.mode === 'preview' ? 'preview' : 'unknown')} />
                <span className="text-sm font-medium">
                  Write proof: {writeResult.mode === 'preview' ? 'Preview' : writeResult.status}
                </span>
              </div>

              {writeResult.mode === 'preview' && (
                <div className="rounded border px-3 py-2 bg-blue-50/50">
                  <p className="text-xs font-medium text-blue-800 mb-1">Preview (no write performed)</p>
                  <div className="text-xs space-y-0.5 text-blue-900">
                    <p>Object: {writeResult.object_type}</p>
                    <p>Label: <code className="bg-blue-100 px-1 rounded">{writeResult.test_label}</code></p>
                    <p>Amount: {writeResult.payload_summary?.amount}</p>
                    <p>Cleanup: {writeResult.cleanup}</p>
                    <p className="text-blue-700 italic">{writeResult.safety}</p>
                  </div>
                </div>
              )}

              {writeResult.mode === 'apply' && writeResult.reason && (
                <p className="text-xs text-orange-700 font-medium">{writeResult.reason}</p>
              )}

              {writeResult.mode === 'apply' && writeResult.summary && (
                <p className={`text-xs font-medium ${
                  writeResult.status === 'ok' ? 'text-green-700' : 'text-red-700'
                }`}>
                  {writeResult.summary}
                </p>
              )}

              {writeResult.orphaned_invoice_id && (
                <div className="rounded border border-red-300 bg-red-50 px-3 py-2">
                  <p className="text-xs font-bold text-red-800">ORPHAN WARNING</p>
                  <p className="text-xs text-red-700">
                    Invoice ID: <code className="bg-red-100 px-1 rounded">{writeResult.orphaned_invoice_id}</code>
                  </p>
                  <p className="text-xs text-red-700">
                    Label: <code className="bg-red-100 px-1 rounded">{writeResult.orphan_label}</code>
                  </p>
                  <p className="text-xs text-red-700 mt-1">
                    Go to Pennylane, find this draft invoice, and delete it manually.
                  </p>
                </div>
              )}

              {/* Steps detail */}
              {writeResult.steps && writeResult.steps.length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer font-medium text-muted-foreground">
                    Steps detail ({writeResult.steps.length})
                  </summary>
                  <pre className="mt-1 border rounded p-2 overflow-x-auto bg-muted/30 max-h-48 overflow-y-auto text-[10px]">
                    {JSON.stringify(writeResult.steps, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


export function PennylaneConnectionTestPanel() {
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [testResults, setTestResults] = useState({});
  const [writeResults, setWriteResults] = useState({});
  const [testingEntity, setTestingEntity] = useState(null);
  const [writingEntity, setWritingEntity] = useState(null);

  const loadEntities = async () => {
    setLoading(true);
    try {
      const { data } = await diagnosticsAPI.pennylaneEntities();
      setEntities(data.entities || []);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadEntities(); }, []);

  const runTest = async (entityId) => {
    setTestingEntity(entityId);
    try {
      const { data } = await diagnosticsAPI.pennylaneConnectionTest(entityId);
      setTestResults((prev) => ({ ...prev, [entityId]: data }));
      if (data.status === 'ok') {
        toast.success(`${entityId}: Read + scope check passed`);
      } else {
        toast.error(`${entityId}: ${data.error || data.status}`);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setTestingEntity(null);
    }
  };

  const runPreview = async (entityId) => {
    setWritingEntity(entityId);
    try {
      const { data } = await diagnosticsAPI.pennylaneWriteProof(entityId, false);
      setWriteResults((prev) => ({ ...prev, [entityId]: data }));
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setWritingEntity(null);
    }
  };

  const runApply = async (entityId) => {
    setWritingEntity(entityId);
    try {
      const { data } = await diagnosticsAPI.pennylaneWriteProof(entityId, true);
      setWriteResults((prev) => ({ ...prev, [entityId]: data }));
      if (data.status === 'ok') {
        toast.success(`${entityId}: Write proof passed (created + deleted)`);
      } else if (data.status === 'refused') {
        toast.error(`${entityId}: Write REFUSED (company mismatch)`);
      } else if (data.status === 'orphan_warning') {
        toast.error(`${entityId}: ORPHAN WARNING, see details`);
      } else {
        toast.error(`${entityId}: ${data.error || data.status}`);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setWritingEntity(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Pennylane Connection Test
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Per-entity READ + SCOPE check and reversible WRITE proof for Pennylane-connected entities.
          Read/scope runs freely. Write proof creates a draft invoice (non-financial, ledger-safe)
          and deletes it immediately. Each entity tested against its own key only.
        </p>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading entities...
          </div>
        ) : entities.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No Pennylane entities found. Check ENTITY_DEFS in config.py.
          </p>
        ) : (
          <div className="space-y-2">
            {entities.map((entity) => (
              <EntityRow
                key={entity.entity_id}
                entity={entity}
                onTest={runTest}
                onPreview={runPreview}
                onApply={runApply}
                testResult={testResults[entity.entity_id]}
                writeResult={writeResults[entity.entity_id]}
                testing={testingEntity === entity.entity_id}
                writing={writingEntity === entity.entity_id}
              />
            ))}
          </div>
        )}

        <Button
          variant="outline"
          size="sm"
          onClick={loadEntities}
          disabled={loading}
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
          Refresh
        </Button>
      </CardContent>
    </Card>
  );
}
